import { Box } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Input from "@ui/input/Input";
import Spacer from "@ui/spacer/Spacer";
import { TypoCaption } from "@ui/text/Typo";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { GD } from "src/GD";
import { StartScreenView } from "../StartScreenView";
import { DS } from "../ds/Kit";
import { S_AUTH_CHANGE_VIEW } from "./AuthView";
import { FormParser } from "src/view/FormParser";
import { ILoginTwoFactorChallenge } from "src/services/AuthService";
import { clearRememberedCredentials, REMEMBERED_CREDENTIALS_KEY } from "src/utils/authStorage";
import { normalizeEmail } from "src/utils/validation";
import { isPublicAuthPath } from "src/utils/authRoutes";
import {
	isRunningInNativeApp,
	openGoogleAuthenticator,
} from "src/utils/nativePlatform";
import {
	AUTH_FORM_ACTIONS_SX,
	AUTH_PRIMARY_BUTTON_SX,
	AUTH_SECONDARY_BUTTON_SX,
	AuthCard,
	AuthDivider,
	AuthStatus,
} from "./AuthLayout";

const passkey = "3fb2afd0add9eaa323654b67d395fe41eb1151c69fd82ef767ce611b0b1f7ba8";

const getForm = async () => {
	const form = {
		login: "",
		$__login_autoComplete: "username",
		password: "",
		$__password_type: "password",
		$__password_autoComplete: "current-password",
		controls: {
			remember: false,
			$__remember_label: "Stay Signed In",
			forgot: "Forgot password",
			$__forgot_type: "href",
		},
		$__controls_gridColumns: "1fr max-content",
		$__controls_blockless: true,
		spacer: "10px",
		$__spacer_type: "spacer",
	};
	try {
		const lf = sessionStorage.getItem(REMEMBERED_CREDENTIALS_KEY);
		if (lf) {
			const f = JSON.parse(lf);
			form.login = f.login;
			const pass = await decryptString(f.password, passkey);
			form.password = pass; //f.password;
		}
	} catch (e) {}
	return form;
};

export const AuthLogin = (props: { login?: string; pass?: string }) => {
	const nativeApp = isRunningInNativeApp();
	const [formData, setFormData] = useState<IAuthForm>();
	const [busy, setBusy] = useState(false);
	const [pendingTwoFactor, setPendingTwoFactor] = useState<{ login: string; password: string; remember: boolean } | null>(null);
	const [twoFactorForm, setTwoFactorForm] = useState<{ totp: string; method: TTwoFactorMethod }>({ totp: "", method: "totp" });
	const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
	const [emailConfirmation, setEmailConfirmation] = useState<string | null>(null);
	const [resendingEmail, setResendingEmail] = useState(false);

	useEffect(() => {
		getForm().then(form => {
			if (props && props.login && props.pass) {
				form.login = props.login;
				form.password = props.pass;
			}
			setPendingTwoFactor(null);
			setTwoFactorForm({ totp: "", method: "totp" });
			setTwoFactorError(null);
			setFormData(form as any as IAuthForm);
		});
	}, [props]);

	const handleVerifyTwoFactor = async () => {
		if (!pendingTwoFactor) return;

		setTwoFactorError(null);
		setBusy(true);
		const response = await GD.REQ_AUTH_2FA_VALIDATE.request(twoFactorForm);
		setBusy(false);

		if (response) {
			setTwoFactorError(response.message || "Failed to verify the authentication code");
			return;
		}

		if (pendingTwoFactor.remember) {
			const encrypted = await encryptString(pendingTwoFactor.password, passkey);
			sessionStorage.setItem(REMEMBERED_CREDENTIALS_KEY, JSON.stringify({ login: pendingTwoFactor.login, password: encrypted }));
		} else {
			clearRememberedCredentials();
		}

		setPendingTwoFactor(null);
		setTwoFactorForm({ totp: "", method: "totp" });
	};

	const handleOpenAuthenticator = async () => {
		setTwoFactorError(null);
		if (await openGoogleAuthenticator()) return;
		setTwoFactorError("Google Authenticator is not installed or could not be opened");
	};

	const resendConfirmation = async () => {
		if (!emailConfirmation || resendingEmail) return;
		setResendingEmail(true);
		const response = await GD.REQ_AUTH_REGISTER_RESEND.request({ email: emailConfirmation });
		setResendingEmail(false);
		if (response.error) {
			setTwoFactorError(response.error.message || "Could not resend the confirmation email");
			return;
		}
		setTwoFactorError(null);
		toast.success("Confirmation email sent");
	};

	return (
		<StartScreenView busy={busy}>
			<AuthCard
				icon={emailConfirmation ? "mail" : pendingTwoFactor ? "shield" : undefined}
				eyebrow={emailConfirmation ? "Email verification" : pendingTwoFactor ? "Verification required" : "Secure sign in"}
				title={emailConfirmation ? "Confirm your email" : pendingTwoFactor ? "Confirm your sign in" : "Welcome back"}
				subtitle={
					emailConfirmation
						? "Open the confirmation link in your inbox before signing in."
						: pendingTwoFactor
						? "Enter the current code from your authenticator app to continue."
						: "Use your Cardpartner credentials to access the operations workspace."
				}>
				{emailConfirmation ? (
					<>
						<AuthStatus tone="warning" title="Email not confirmed">
							We sent a confirmation link to <b>{emailConfirmation}</b>.
						</AuthStatus>
						{twoFactorError ? <><Spacer size="12px" /><AuthStatus tone="error">{twoFactorError}</AuthStatus></> : null}
						<Spacer size="20px" />
						<Button
							title={resendingEmail ? "Sending…" : "Resend confirmation email"}
							variant="primary"
							disabled={resendingEmail}
							onClick={() => void resendConfirmation()}
							sx={AUTH_PRIMARY_BUTTON_SX}
						/>
						<Spacer size="10px" />
						<Button
							title="Back to sign in"
							variant="secondary"
							onClick={() => {
								setEmailConfirmation(null);
								setTwoFactorError(null);
							}}
							sx={AUTH_SECONDARY_BUTTON_SX}
						/>
					</>
				) : pendingTwoFactor ? (
					<>
						<AuthStatus title="Two-factor authentication">
							The code is required for {pendingTwoFactor.login}.
						</AuthStatus>
						{nativeApp && (
							<>
								<Spacer size="12px" />
								<Button
									title="Open Google Authenticator"
									variant="secondary"
									disabled={busy}
									onClick={handleOpenAuthenticator}
									sx={AUTH_SECONDARY_BUTTON_SX}
								/>
							</>
						)}
						<Spacer size="18px" />
						<Input
							data={twoFactorForm}
							name="totp"
							autoComplete="one-time-code"
							label="Authenticator code"
							onChange={value => setTwoFactorForm({ totp: value, method: "totp" })}
							placeholder="6-digit code"
						/>
						{twoFactorError && (
							<>
								<Spacer size="14px" />
								<AuthStatus tone="error">{twoFactorError}</AuthStatus>
							</>
						)}
						<Spacer size="20px" />
						<Button
							title={busy ? "Verifying..." : "Verify code"}
							variant="primary"
							disabled={busy}
							onClick={handleVerifyTwoFactor}
							sx={AUTH_PRIMARY_BUTTON_SX}
						/>
						<Spacer size="10px" />
						<Button
							title="Back to credentials"
							variant="secondary"
							disabled={busy}
							onClick={() => {
								setPendingTwoFactor(null);
								setTwoFactorForm({ totp: "", method: "totp" });
								setTwoFactorError(null);
							}}
							sx={AUTH_SECONDARY_BUTTON_SX}
						/>
					</>
				) : (
					formData && (
						<FormParser
							key={`authForm-${(formData as any).mask_email !== undefined ? "mask" : "normal"}`}
							ignoreObserver={true}
							fullSizeButtons={true}
							actionsSx={AUTH_FORM_ACTIONS_SX}
							secondaryButton={null}
							primaryButton={{
									title: "Sign in",
									variant: "primary",
									sx: {
										...AUTH_PRIMARY_BUTTON_SX,
										flexGrow: 1,
									},
							}}
							onComponentClick={cmp => {
								if (cmp.name === "forgot") {
									S_AUTH_CHANGE_VIEW.invoke("forgot");
								}
							}}
							onSubmit={async (data: IAuthForm): Promise<any> => {
								setTwoFactorError(null);
								setBusy(true);
								const response = await GD.REQ_AUTH_LOGIN.request(data);
								setBusy(false);
								if (response) {
									if (isTwoFactorChallenge(response)) {
										setPendingTwoFactor({
											login: data.login,
											password: data.password,
											remember: !!data.controls?.remember,
										});
										setTwoFactorForm({ totp: "", method: "totp" });
										setTwoFactorError("Enter the code from your authenticator app to continue");
										return {
											data,
											error: {
												message: "Enter the code from your authenticator app to continue",
											},
										};
									}
									if (response.message === "IsMasking") {
										const newForm = {
											login: data.login,
											$__login_autoComplete: "username",
											password: data.password,
											$__password_type: "password",
											$__password_autoComplete: "current-password",
											mask_email: "",
										$__mask_email_label: "Account email",
											$__mask_email_type: "email",
											$__mask_email_autoComplete: "email",
											controls: data.controls,
											$__controls_gridColumns: "1fr max-content",
											$__controls_blockless: true,
											spacer: "10px",
											$__spacer_type: "spacer",
										};
										setFormData(newForm as any);
									return { data: newForm, error: { message: "Enter the account email requested for this sign-in." } };
									}
									if (response.code === 4001) {
										setPendingTwoFactor({
											login: data.login,
											password: data.password,
											remember: !!data.controls?.remember,
										});
										setTwoFactorForm({ totp: "", method: "totp" });
										setTwoFactorError(response.message || "Enter the code from your authenticator app to continue");
										return {
											data,
											error: {
												message: response.message || "Enter the code from your authenticator app to continue",
											},
										};
									}
									// 2803 = e-mail not confirmed yet. Point the user to the
									// confirmation flow instead of a bare error message.
									if (response.code === 2803) {
										setEmailConfirmation(normalizeEmail(data.login));
										return { data };
									}
									return { data: data, error: response };
								}
								const encrypted = await encryptString(data.password, passkey);
								if (data.controls.remember) {
									sessionStorage.setItem(REMEMBERED_CREDENTIALS_KEY, JSON.stringify({ login: data.login, password: encrypted }));
								} else {
									clearRememberedCredentials();
								}
								// Signing in from a public auth URL (landing links to /signup) must
								// leave it, or GlobalView keeps rendering AuthView after login.
								if (isPublicAuthPath(window.location.pathname)) {
									window.history.replaceState({}, "", "/");
									window.dispatchEvent(new PopStateEvent("popstate"));
								}
								return { data };
							}}
							onRequest={async (): Promise<any> => {
								return formData;
							}}
						/>
					)
				)}

				{!pendingTwoFactor && !emailConfirmation && (
					<>
						<AuthDivider label="New to Cardpartner?" />
						<Button
							title="Sign up"
							variant="secondary"
							onClick={() => S_AUTH_CHANGE_VIEW.invoke("signup")}
							sx={AUTH_SECONDARY_BUTTON_SX}
						/>
					</>
				)}
				{!pendingTwoFactor && !emailConfirmation ? (
					<Box sx={{ marginTop: "18px", textAlign: "center" }}>
						<TypoCaption sx={{ color: DS.muted, fontSize: "12px", lineHeight: 1.45 }}>
							Two-factor verification may be required.
						</TypoCaption>
					</Box>
				) : null}
			</AuthCard>
		</StartScreenView>
	);
};

// CRYPTO
export type EncryptedPayload = {
	s: string /** salt, base64 */;
	iv: string /** iv (nonce), base64 */;
	ct: string /** ciphertext, base64 */;
};

const isTwoFactorChallenge = (value: unknown): value is ILoginTwoFactorChallenge => {
	return !!value && typeof value === "object" && "twofa_required" in value && (value as ILoginTwoFactorChallenge).twofa_required === true;
};

const te = new TextEncoder();
const td = new TextDecoder();

const toB64 = (buf: ArrayBuffer | Uint8Array): string => {
	const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
	let s = "";
	for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
	return btoa(s);
};

const fromB64 = (b64: string): Uint8Array => {
	const bin = atob(b64);
	const u8 = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
	return u8;
};

async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
	const keyMaterial = await crypto.subtle.importKey("raw", te.encode(password), "PBKDF2", false, ["deriveKey"]);
	return crypto.subtle.deriveKey(
		{
			name: "PBKDF2",
			salt: salt as any,
			iterations: 150_000,
			hash: "SHA-256",
		},
		keyMaterial,
		{ name: "AES-GCM", length: 256 },
		false, // not extractable
		["encrypt", "decrypt"],
	);
}

/** ENCRYPT */
export async function encryptString(plaintext: string, password: string): Promise<EncryptedPayload> {
	const salt = crypto.getRandomValues(new Uint8Array(16));
	const iv = crypto.getRandomValues(new Uint8Array(12));
	const key = await deriveKey(password, salt);

	const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, te.encode(plaintext));

	return { s: toB64(salt), iv: toB64(iv), ct: toB64(ct) };
}

/** DECRYPT */
export async function decryptString(payload: EncryptedPayload, password: string): Promise<string> {
	const salt = fromB64(payload.s);
	const iv = fromB64(payload.iv) as any;
	const key = await deriveKey(password, salt);
	const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, fromB64(payload.ct) as any);
	return td.decode(pt);
}

/*
async function demo() {
	const encrypted = await encryptString("Hello, WebCrypto + TS!", "myStrongPassword");
	console.log("encrypted:", encrypted);

	const decrypted = await decryptString(encrypted, "myStrongPassword");
	console.log("decrypted:", decrypted);
}
demo().catch(console.error);
*/
